/**
 * Organization Admin Panel page for organization administration
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { MainLayout } from '@/components/Layout/MainLayout';
import { OrganizationAdminUsersTab } from '@/components/OrganizationAdmin/OrganizationAdminUsersTab';
import { useAuth } from '@/hooks/useAuth';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { UserRole } from '@/types';
import { Navigate } from 'react-router-dom';

export const OrganizationAdminPanel: React.FC = () => {
  useRegisterPageHelp('Organization Administration');
  const { user, isLoading } = useAuth();
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    console.log('=== OrganizationAdminPanel Debug ===');
    console.log('isLoading:', isLoading);
    console.log('User:', user);
    console.log('User role:', user?.role);
    console.log('User role type:', typeof user?.role);
    console.log('UserRole.ORGANIZATION_ADMIN:', UserRole.ORGANIZATION_ADMIN);
    console.log('Is org admin check:', user?.role === UserRole.ORGANIZATION_ADMIN || user?.role === 'organization_admin');
    console.log('===================================');
  }, [isLoading, user]);

  // Wait for user to load - don't redirect if still loading
  if (isLoading) {
    console.log('OrganizationAdminPanel: Still loading...');
    return <Box>Loading...</Box>;
  }

  // Only redirect if we're sure there's no user (not loading and no user)
  if (!user) {
    console.log('OrganizationAdminPanel: No user found after loading, redirecting to /app/history');
    return <Navigate to="/app/history" replace />;
  }

  const isOrgAdmin = user.role === UserRole.ORGANIZATION_ADMIN || user.role === 'organization_admin';

  console.log('OrganizationAdminPanel: User check', {
    user: user.email,
    role: user.role,
    isOrgAdmin,
    UserRole_ORGANIZATION_ADMIN: UserRole.ORGANIZATION_ADMIN,
    roleEqualsEnum: user.role === UserRole.ORGANIZATION_ADMIN,
    roleEqualsString: user.role === 'organization_admin'
  });

  if (!isOrgAdmin) {
    console.error('OrganizationAdminPanel: User is not organization admin! Role:', user.role, 'Type:', typeof user.role);
    return <Navigate to="/app/history" replace />;
  }

  console.log('✓ OrganizationAdminPanel rendered successfully for organization admin user');

  const handleCopyGuid = () => {
    if (user?.organization_id) {
      navigator.clipboard.writeText(user.organization_id);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Organization Administration
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage users in your organization
        </Typography>

        {copySuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCopySuccess(false)}>
            Organization GUID copied to clipboard
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Organization Registration Code
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this code with new users so they can register and join your organization.
            They need to enter this GUID in the "Organization GUID" field during registration.
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              fullWidth
              value={user?.organization_id || ''}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      size="small"
                      onClick={handleCopyGuid}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'action.hover',
                  fontFamily: 'monospace',
                },
              }}
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <OrganizationAdminUsersTab />
        </Paper>
      </Box>
    </MainLayout>
  );
};
